import { inject, Injectable } from '@angular/core';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { delay, take } from 'rxjs/operators';

import { StorageService } from '@pe/checkout/storage';
import { CheckoutWindow, CheckoutSettingsInterface } from '@pe/checkout/types';

import { ACCESS_TOKEN_NAME } from '../auth';
import { InitFlow } from '../flow/flow.actions';
import { FlowState } from '../flow/flow.state';

import { GetSettings, InitSettings } from './settings.actions';
import { SettingsState } from './settings.state';

@Injectable({
  providedIn: 'root',
})
export class SettingsActionHandler {
  private readonly storage = inject(StorageService);
  private readonly loader = (window as CheckoutWindow).pe_pageCheckoutLoader;


  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
  ) {
    if (this.loader?.checkoutData) {
      this.store.dispatch(new InitSettings(this.loader.checkoutData));
    } else if (this.loader) {
      this.loader.successCallback = (data: CheckoutSettingsInterface) => {
        this.store.dispatch(new InitSettings(data));
      };

      this.loader.errorCallback = () => {
        this.storage.remove(ACCESS_TOKEN_NAME);
        this.loadSettingsFallback();
      };

      if (this.loader.lastError || !this.loader.processed) {
        this.loadSettingsFallback();
      }

      // The loader requests /full-settings right after the flow callback; if it never did, load settings ourselves
      this.actions$.pipe(
        ofActionSuccessful(InitFlow),
        take(1),
        delay(0),
      ).subscribe(() => {
        !this.loader.processed && this.loadSettingsFallback();
      });
    }
  }

  // show-wrapper skips GetSettings when the flow came from the loader, so a failed loader request leaves settings empty
  private loadSettingsFallback(): void {
    const channelSetId = this.store.selectSnapshot(FlowState.flow)?.channelSetId;
    channelSetId && !this.store.selectSnapshot(SettingsState.settings)
      && this.store.dispatch(new GetSettings(channelSetId));
  }
}
